import type { ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, ChevronLeft, ChevronRight, Loader2, Pencil, Plus, Printer, Settings2 } from "lucide-react";
import { BreadcrumbAncestry } from "./ActionControlPanel";
import type { Crumb } from "./ActionControlPanel";
import { QuickMetaPopover } from "./QuickMetaPopover";
import { RecordStrip } from "./RecordStrip";
import { RelatedDropdown } from "./RelatedDropdown";
import { confirm as confirmDialog } from "./ConfirmDialog";
import { toast } from "../react/hooks/use-toast";
import { useOptionalFormStateContext } from "../react/form/RecordProvider";
import type { FormButtonElement, FormRelatedChip, RecordData } from "../core";

interface FormControlPanelProps {
    // Ancestor trail (e.g. the action name) — rendered as a small line above
    // the current title. Empty for top-level records.
    crumbs: Crumb[];
    // The current crumb / record name — rendered as the larger title beneath
    // the ancestor line, matching the canonical breadcrumb layout.
    title: string;
    onCreate?: () => void;
    // Called when the user discards a brand-new (unsaved) record draft. New
    // drafts have no read-mode to fall back to, so discarding must navigate
    // away (back to the list) rather than just exit edit-mode. Existing
    // records ignore this and revert to read-mode via `form.onDiscard()`.
    onDiscardNew?: () => void;
    // Pager — show when we know the record's position in the surrounding
    // list. Hidden otherwise (e.g. deep-linked to a record without a list
    // context loaded).
    pagerVisible?: boolean;
    pagerIndex?: number; // 1-based
    pagerTotal?: number;
    onPrev?: () => void;
    onNext?: () => void;
    canPrev?: boolean;
    canNext?: boolean;
    // Form-specific menu surfaces. Custom items (action / model verbs) come
    // from the action manifest later; for now the menus surface only the
    // standard items and any provided customs.
    customPrintItems?: PrintItem[];
    customActionItems?: ActionItem[];
    onDuplicate?: () => void;
    onArchive?: () => void;
    onDelete?: () => void;
    // Quick-verb buttons in the action bar (Phase 4 will wire workflow
    // transitions and form_action_bar verbs). Renders nothing if absent.
    verbs?: VerbButton[];
    // Workflow pipeline stepper — rendered when stages are known. Each entry
    // is one stage label; pass `currentIndex` to highlight progress.
    stepper?: StepperSpec;
    // Quick Meta View (Enhancement 05) — when these are supplied, a small (i)
    // icon renders next to the record title; click opens a popover with the
    // record's create/update meta + "View full history" link. All three are
    // optional so the panel still renders when meta-view isn't applicable
    // (e.g. on new-record drafts or contexts without a loaded record).
    record?: RecordData | null | undefined;
    modelKey?: string;
    recordUuid?: string;
    isNewRecord?: boolean;
    // Related ▾ dropdown items (Enhancement 06). When supplied + recordUuid
    // is a real uuid, the toolbar renders the `Related ▾` trigger as the
    // leftmost dropdown (before Print / Actions). When empty or undefined,
    // nothing renders.
    relatedItems?: FormRelatedChip[];
    // Record-strip chips (communication Enh 03 Slice 2) — the gated
    // `activity` component list from the form render plan. Renders in the
    // toolbar's middle slot (after the breadcrumb). Empty / undefined, or a
    // new unsaved record → nothing renders.
    activityComponents?: string[];
    // Strip chip jump → the record timeline below the sheet (Enh 03 S4).
    onTimelineJump?: (filter: import("./RecordStrip").TimelineFilter) => void;
    // Chatter mutations (upload/follow/…) → timeline refetch signal.
    onChatterMutated?: () => void;
    // Workflow-driven record (model has a workflow=True field) — ⑆ chip.
    hasWorkflow?: boolean;
    // Workflow drawer footer next-step buttons (Enh 14 Stage 4) — the record's
    // action verbs + the run handlers, threaded down to the drawer footer.
    headerButtons?: FormButtonElement[];
    onRunButton?: (button: FormButtonElement) => void;
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
    actionSlug?: string;
    // External chatter epoch — strip counts refetch on composer posts.
    chatterEpoch?: number;
}

export interface PrintItem {
    key: string;
    label: string;
    onSelect: () => void;
}

export interface ActionItem {
    key: string;
    label: string;
    danger?: boolean;
    onSelect: () => void;
}

export interface VerbButton {
    key: string;
    label: string;
    variant?: "positive" | "negative" | "neutral";
    onClick: () => void;
}

export interface StepperSpec {
    stages: string[];          // ordered pipeline stages
    optionalStages?: string[]; // alternate end-states (Lost, On Hold, …)
    currentIndex: number;      // -1 if current is in optional / outside flow
    onPickStage?: (label: string) => void;
}

// Form-mode control panel — two stacked rows above the FormView body,
// matching the canonical prototype:
//   1. Toolbar: + New | breadcrumb | spacer | Print ▾ | Actions ▾ | pager
//   2. Action bar: quick verbs (left)  |  workflow stepper (right)
//
// The action bar collapses to nothing if no verbs or stepper are provided,
// so deep links and records without workflow render cleanly.
export function FormControlPanel(props: FormControlPanelProps) {
    const {
        crumbs,
        title,
        onCreate,
        onDiscardNew,
        pagerVisible,
        pagerIndex,
        pagerTotal,
        onPrev,
        onNext,
        canPrev,
        canNext,
        customPrintItems = [],
        customActionItems = [],
        onDuplicate,
        onArchive,
        onDelete,
        verbs = [],
        stepper,
        record,
        modelKey,
        recordUuid,
        isNewRecord,
        relatedItems = [],
        activityComponents,
        onTimelineJump,
        onChatterMutated,
        hasWorkflow,
        headerButtons,
        onRunButton,
        onRunTransition,
        actionSlug,
        chatterEpoch,
    } = props;

    // Edit-cycle state — pulled from the surrounding RecordProvider when
    // present. Outside a provider (e.g. legacy callers), the buttons
    // simply don't render, preserving backward compatibility.
    const form = useOptionalFormStateContext();
    const isEditing = form?.isEditing ?? false;
    const onDiscardClick = async () => {
        if (!form) return;
        if (form.isDirty) {
            const ok = await confirmDialog({
                title: "Discard changes?",
                message: "Unsaved changes will be lost. Continue?",
                confirmLabel: "Discard",
                cancelLabel: "Keep editing",
            });
            if (!ok) return;
        }
        // New drafts have nothing to revert to — discarding cancels the
        // creation and navigates back to the list. Existing records just
        // drop edit-mode and revert to the loaded values.
        if (form.isNew && onDiscardNew) {
            onDiscardNew();
            return;
        }
        form.onDiscard();
    };
    const onSaveClick = async () => {
        if (!form) return;
        const result = await form.onSave();
        if (result.ok) return;
        // Validation failures (blank required fields) surface as a side
        // toast listing every missing field — the fields themselves are
        // already painted red inline by FormSheet (form.invalidFields). No
        // modal: the user fixes the highlighted fields in place. Genuine
        // server / unexpected failures fall through to a plain error toast.
        if (result.missing && result.missing.length > 0) {
            toast({
                variant: "destructive",
                title: result.missing.length === 1 ? "1 required field missing" : `${result.missing.length} required fields missing`,
                description: result.missing.join(", "),
            });
        } else {
            toast({
                variant: "destructive",
                title: "Could not save",
                description: result.error,
            });
        }
    };

    const hasActionBar = verbs.length > 0 || (stepper && stepper.stages.length > 0);

    return (
        <>
            <div className="form-toolbar">
                {!isEditing ? (
                    <div className="form-toolbar__group">
                        {onCreate ? (
                            <button type="button" className="btn form-toolbar__new" onClick={onCreate}>
                                <Plus size={14} />
                                <span>New</span>
                            </button>
                        ) : null}
                        {form ? (
                            <button
                                type="button"
                                className="btn form-toolbar__edit"
                                onClick={form.onEdit}
                                aria-label="Edit"
                            >
                                <Pencil size={13} />
                                <span>Edit</span>
                            </button>
                        ) : null}
                    </div>
                ) : form ? (
                    <div className="form-toolbar__group">
                        <EditCycleButtons
                            isDirty={form.isDirty}
                            isSaving={form.isSaving}
                            isNew={form.isNew}
                            onSave={() => void onSaveClick()}
                            onDiscard={() => void onDiscardClick()}
                        />
                    </div>
                ) : null}
                <Breadcrumb
                    crumbs={crumbs}
                    title={title}
                    record={record}
                    modelKey={modelKey}
                    recordUuid={recordUuid}
                    isNewRecord={isNewRecord}
                />

                {/* Record-strip chips (communication Enh 03) — fill the toolbar's
                    empty middle between the breadcrumb and Related/Print/Actions.
                    Hidden while editing so the save/discard cycle stays focused. */}
                {!isEditing && modelKey ? (
                    <RecordStrip
                        components={activityComponents ?? []}
                        modelKey={modelKey}
                        recordUuid={isNewRecord ? undefined : recordUuid}
                        recordTitle={title}
                        toolbar
                        hasWorkflow={hasWorkflow}
                        headerButtons={headerButtons}
                        onRunButton={onRunButton}
                        onRunTransition={onRunTransition}
                        actionSlug={actionSlug}
                        countsRefresh={chatterEpoch}
                        onTimelineJump={onTimelineJump}
                        onMutated={onChatterMutated}
                    />
                ) : null}

                <div className="form-toolbar__right">
                    {!isEditing ? (
                        <>
                            {relatedItems.length > 0 ? (
                                <RelatedDropdown
                                    items={relatedItems}
                                    activeId={recordUuid}
                                    activeModelKey={modelKey}
                                    record={record}
                                    enabled={Boolean(recordUuid) && !isNewRecord}
                                />
                            ) : null}
                            <PrintMenu customItems={customPrintItems} />
                            <ActionsMenu
                                customItems={customActionItems}
                                onDuplicate={onDuplicate}
                                onArchive={onArchive}
                                onDelete={onDelete}
                            />

                            {pagerVisible ? (
                                <>
                                    <div className="form-toolbar__sep" />
                                    <div className="pager">
                                        {pagerIndex && pagerTotal ? (
                                            <span className="pager__count tabular">
                                                {pagerIndex} / {pagerTotal}
                                            </span>
                                        ) : null}
                                        <div className="pager__nav">
                                            <button
                                                type="button"
                                                className="pager__btn"
                                                onClick={onPrev}
                                                disabled={!canPrev}
                                                aria-label="Previous record"
                                            >
                                                <ChevronLeft size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                className="pager__btn"
                                                onClick={onNext}
                                                disabled={!canNext}
                                                aria-label="Next record"
                                            >
                                                <ChevronRight size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>

            {hasActionBar ? (
                <div className="form-actionbar">
                    {verbs.length > 0 ? (
                        <div className="form-actionbar__verbs">
                            {verbs.map((v) => (
                                <button
                                    key={v.key}
                                    type="button"
                                    className={`btn ${
                                        v.variant === "positive"
                                            ? "btn--positive"
                                            : v.variant === "negative"
                                                ? "btn--negative"
                                                : ""
                                    } form-actionbar__btn`}
                                    onClick={v.onClick}
                                >
                                    {v.label}
                                </button>
                            ))}
                        </div>
                    ) : null}

                    {stepper && stepper.stages.length > 0 ? (
                        <Stepper {...stepper} />
                    ) : null}
                </div>
            ) : null}
        </>
    );
}

// Edit-cycle buttons — Save (primary) + Discard (subtle). The save button
// shows a spinner while in flight + disables to swallow double-clicks. The
// primary verb always reads "Save" — on new-record drafts it stays enabled
// (via `isNew`) even before the draft is dirtied.
function EditCycleButtons({
    isDirty,
    isSaving,
    isNew,
    onSave,
    onDiscard,
}: {
    isDirty: boolean;
    isSaving: boolean;
    isNew: boolean;
    onSave: () => void;
    onDiscard: () => void;
}) {
    const saveLabel = "Save";
    return (
        <>
            <button
                type="button"
                className="btn form-toolbar__save"
                onClick={onSave}
                disabled={isSaving || (!isDirty && !isNew)}
                aria-label={saveLabel}
                data-viewid="ede-form-save"
            >
                {isSaving ? <Loader2 size={13} className="form-toolbar__save-spinner" /> : null}
                <span>{saveLabel}</span>
            </button>
            <button
                type="button"
                className="btn form-toolbar__discard"
                onClick={onDiscard}
                disabled={isSaving}
                aria-label="Discard"
                data-viewid="ede-form-discard"
            >
                <span>Discard</span>
            </button>
        </>
    );
}

// Stacked breadcrumb — ancestor trail on a small muted line above, current
// title as the larger primary line beneath. Ancestor rendering (incl. the `…`
// overflow-dropdown collapse rule) is shared with ActionControlPanel via
// `BreadcrumbAncestry` so multi-record and form modes stay visually identical.
// When record metadata is supplied (Enhancement 05), a (i) icon renders right
// of the title, opening a popover with create / update audit info.
function Breadcrumb({
    crumbs,
    title,
    record,
    modelKey,
    recordUuid,
    isNewRecord,
}: {
    crumbs: Crumb[];
    title: string;
    record?: RecordData | null | undefined;
    modelKey?: string;
    recordUuid?: string;
    isNewRecord?: boolean;
}) {
    const showMeta = Boolean(modelKey) && Boolean(recordUuid);
    return (
        <div className="breadcrumbs">
            <BreadcrumbAncestry crumbs={crumbs} />
            <div className="breadcrumbs__title">
                <span>{title}</span>
                {showMeta ? (
                    <QuickMetaPopover
                        record={record}
                        isNew={Boolean(isNewRecord)}
                        modelKey={modelKey as string}
                        recordUuid={recordUuid as string}
                    />
                ) : null}
            </div>
        </div>
    );
}

// Print menu — populated exclusively from the live /api/reports/_list lister
// (mirrors the old frontend). When the action has no registered reports for
// the current (model, record), the button hides entirely instead of opening
// an empty / stubbed menu.
function PrintMenu({ customItems }: { customItems: PrintItem[] }) {
    if (customItems.length === 0) return null;
    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button type="button" className="btn form-toolbar__actions" aria-label="Print">
                    <Printer size={13} />
                    <span>Print</span>
                    <ChevronDown size={11} className="form-toolbar__actions-chev" />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content className="rdx-menu" align="end" sideOffset={4} collisionPadding={12}>
                    {customItems.map((it) => (
                        <DropdownMenu.Item key={it.key} className="rdx-menu__item" onSelect={it.onSelect}>
                            <span className="rdx-menu__item-primary">{it.label}</span>
                        </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

function ActionsMenu({
    customItems,
    onDuplicate,
    onArchive,
    onDelete,
}: {
    customItems: ActionItem[];
    onDuplicate?: () => void;
    onArchive?: () => void;
    onDelete?: () => void;
}) {
    const standardCount = [onDuplicate, onArchive, onDelete].filter(Boolean).length;
    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button type="button" className="btn form-toolbar__actions" aria-label="Record actions">
                    <Settings2 size={13} />
                    <span>Actions</span>
                    <ChevronDown size={11} className="form-toolbar__actions-chev" />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content className="rdx-menu" align="end" sideOffset={4} collisionPadding={12}>
                    {onDuplicate ? (
                        <DropdownMenu.Item className="rdx-menu__item" onSelect={onDuplicate}>
                            <span className="rdx-menu__item-primary">Duplicate</span>
                        </DropdownMenu.Item>
                    ) : null}
                    {onArchive ? (
                        <DropdownMenu.Item className="rdx-menu__item" onSelect={onArchive}>
                            <span className="rdx-menu__item-primary">Archive</span>
                        </DropdownMenu.Item>
                    ) : null}
                    {onDelete ? (
                        <DropdownMenu.Item className="rdx-menu__item" onSelect={onDelete}>
                            <span className="rdx-menu__item-primary" style={{ color: "var(--negative)" }}>
                                Delete
                            </span>
                        </DropdownMenu.Item>
                    ) : null}
                    {/* Standard actions above; custom record-bound actions
                        (Enhancement 10) render below the separator. */}
                    {customItems.length > 0 && standardCount > 0 ? <div className="menu-group__sep" role="separator" /> : null}
                    {customItems.map((it) => (
                        <DropdownMenu.Item
                            key={it.key}
                            className="rdx-menu__item"
                            onSelect={it.onSelect}
                        >
                            <span className="rdx-menu__item-primary" style={it.danger ? { color: "var(--negative)" } : undefined}>
                                {it.label}
                            </span>
                        </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

function Stepper({ stages, optionalStages = [], currentIndex, onPickStage }: StepperSpec): ReactNode {
    return (
        <div className="form-stepper" role="progressbar">
            {stages.map((s, i) => {
                const done = currentIndex >= 0 && i < currentIndex;
                const current = currentIndex >= 0 && i === currentIndex;
                return (
                    <button
                        type="button"
                        key={s}
                        className={`form-stepper__step ${done ? "is-done" : ""} ${current ? "is-current" : ""}`}
                        onClick={() => onPickStage?.(s)}
                        disabled={!onPickStage}
                    >
                        {s}
                    </button>
                );
            })}
            {optionalStages.length > 0 ? (
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                        <button type="button" className="form-stepper__more" aria-label="More stages">…</button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                        <DropdownMenu.Content className="rdx-menu" align="end" sideOffset={4} collisionPadding={12}>
                            {optionalStages.map((s) => (
                                <DropdownMenu.Item key={s} className="rdx-menu__item" onSelect={() => onPickStage?.(s)}>
                                    <span className="rdx-menu__item-primary">{s}</span>
                                </DropdownMenu.Item>
                            ))}
                        </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                </DropdownMenu.Root>
            ) : null}
        </div>
    );
}
